import { ViewerSettings } from '../types/settings';

type LanguageCode = ViewerSettings['language'];

type I18nKey =
  | 'settings.title'
  | 'settings.close'
  | 'settings.appearance'
  | 'settings.theme'
  | 'settings.darkMode'
  | 'settings.lightMode'
  | 'settings.language'
  | 'settings.selectLanguage'
  | 'settings.accessibility'
  | 'settings.textAndIconSize'
  | 'settings.help'
  | 'settings.replayTutorial'
  | 'settings.system'
  | 'settings.resetViewer'
  | 'settings.option.small'
  | 'settings.option.medium'
  | 'settings.option.large'
  | 'step.label'
  | 'step.of'
  | 'header.adminMode'
  | 'header.adminModeEnabled'
  | 'header.administrativeView'
  | 'header.settings'
  | 'header.close'
  | 'panel.tools'
  | 'panel.parts'
  | 'panel.items'
  | 'panel.searchTools'
  | 'panel.searchParts'
  | 'panel.viewDetailsFor'
  | 'toolbar.tools'
  | 'toolbar.parts'
  | 'toolbar.narrationOn'
  | 'toolbar.narrationOff'
  | 'toolbar.timeline'
  | 'toolbar.stepVisibility'
  | 'toolbar.uploadGlb'
  | 'toolbar.tutorial'
  | 'toolbar.customerSupport';

const dictionary: Record<LanguageCode, Record<I18nKey, string>> = {
  en: {
    'settings.title': 'Settings',
    'settings.close': 'Close settings',
    'settings.appearance': 'Appearance',
    'settings.theme': 'Theme',
    'settings.darkMode': 'Dark mode',
    'settings.lightMode': 'Light mode',
    'settings.language': 'Language',
    'settings.selectLanguage': 'Select Language',
    'settings.accessibility': 'Accessibility',
    'settings.textAndIconSize': 'Text and Icon Size',
    'settings.help': 'Help',
    'settings.replayTutorial': 'Replay Tutorial',
    'settings.system': 'System',
    'settings.resetViewer': 'Reset Viewer',
    'settings.option.small': 'Small',
    'settings.option.medium': 'Medium',
    'settings.option.large': 'Large',
    'step.label': 'Step',
    'step.of': 'of',
    'header.adminMode': 'Admin Mode',
    'header.adminModeEnabled': 'Admin mode enabled',
    'header.administrativeView': 'Administrative view',
    'header.settings': 'Settings',
    'header.close': 'Close',
    'panel.tools': 'Tools',
    'panel.parts': 'Parts',
    'panel.items': 'Items',
    'panel.searchTools': 'Search tools...',
    'panel.searchParts': 'Search parts...',
    'panel.viewDetailsFor': 'View details for',
    'toolbar.tools': 'Tools',
    'toolbar.parts': 'Parts List',
    'toolbar.narrationOn': 'Narration On',
    'toolbar.narrationOff': 'Narration Off',
    'toolbar.timeline': 'Timeline Editor (Admin)',
    'toolbar.stepVisibility': 'Step Visibility',
    'toolbar.uploadGlb': 'Upload Pipeline GLB',
    'toolbar.tutorial': 'Tutorial',
    'toolbar.customerSupport': 'Customer Support',
  },
  es: {
    'settings.title': 'Configuración',
    'settings.close': 'Cerrar configuración',
    'settings.appearance': 'Apariencia',
    'settings.theme': 'Tema',
    'settings.darkMode': 'Modo oscuro',
    'settings.lightMode': 'Modo claro',
    'settings.language': 'Idioma',
    'settings.selectLanguage': 'Seleccionar idioma',
    'settings.accessibility': 'Accesibilidad',
    'settings.textAndIconSize': 'Tamaño de texto e íconos',
    'settings.help': 'Ayuda',
    'settings.replayTutorial': 'Repetir tutorial',
    'settings.system': 'Sistema',
    'settings.resetViewer': 'Restablecer visor',
    'settings.option.small': 'Pequeño',
    'settings.option.medium': 'Mediano',
    'settings.option.large': 'Grande',
    'step.label': 'Paso',
    'step.of': 'de',
    'header.adminMode': 'Modo administrador',
    'header.adminModeEnabled': 'Modo administrador activado',
    'header.administrativeView': 'Vista administrativa',
    'header.settings': 'Configuración',
    'header.close': 'Cerrar',
    'panel.tools': 'Herramientas',
    'panel.parts': 'Piezas',
    'panel.items': 'Elementos',
    'panel.searchTools': 'Buscar herramientas...',
    'panel.searchParts': 'Buscar piezas...',
    'panel.viewDetailsFor': 'Ver detalles de',
    'toolbar.tools': 'Herramientas',
    'toolbar.parts': 'Lista de piezas',
    'toolbar.narrationOn': 'Narración activada',
    'toolbar.narrationOff': 'Narración desactivada',
    'toolbar.timeline': 'Editor de línea de tiempo (Admin)',
    'toolbar.stepVisibility': 'Visibilidad de pasos',
    'toolbar.uploadGlb': 'Subir GLB del pipeline',
    'toolbar.tutorial': 'Tutorial',
    'toolbar.customerSupport': 'Soporte al cliente',
  },
  fr: {
    'settings.title': 'Paramètres',
    'settings.close': 'Fermer les paramètres',
    'settings.appearance': 'Apparence',
    'settings.theme': 'Thème',
    'settings.darkMode': 'Mode sombre',
    'settings.lightMode': 'Mode clair',
    'settings.language': 'Langue',
    'settings.selectLanguage': 'Sélectionner la langue',
    'settings.accessibility': 'Accessibilité',
    'settings.textAndIconSize': 'Taille du texte et des icônes',
    'settings.help': 'Aide',
    'settings.replayTutorial': 'Relancer le tutoriel',
    'settings.system': 'Système',
    'settings.resetViewer': 'Réinitialiser le visualiseur',
    'settings.option.small': 'Petit',
    'settings.option.medium': 'Moyen',
    'settings.option.large': 'Grand',
    'step.label': 'Étape',
    'step.of': 'sur',
    'header.adminMode': 'Mode admin',
    'header.adminModeEnabled': 'Mode administrateur activé',
    'header.administrativeView': 'Vue administrative',
    'header.settings': 'Paramètres',
    'header.close': 'Fermer',
    'panel.tools': 'Outils',
    'panel.parts': 'Pièces',
    'panel.items': 'Éléments',
    'panel.searchTools': 'Rechercher des outils...',
    'panel.searchParts': 'Rechercher des pièces...',
    'panel.viewDetailsFor': 'Voir les détails de',
    'toolbar.tools': 'Outils',
    'toolbar.parts': 'Liste des pièces',
    'toolbar.narrationOn': 'Narration activée',
    'toolbar.narrationOff': 'Narration désactivée',
    'toolbar.timeline': 'Éditeur de chronologie (Admin)',
    'toolbar.stepVisibility': 'Visibilité des étapes',
    'toolbar.uploadGlb': 'Importer le GLB pipeline',
    'toolbar.tutorial': 'Tutoriel',
    'toolbar.customerSupport': 'Support client',
  },
  de: {
    'settings.title': 'Einstellungen',
    'settings.close': 'Einstellungen schließen',
    'settings.appearance': 'Darstellung',
    'settings.theme': 'Thema',
    'settings.darkMode': 'Dunkelmodus',
    'settings.lightMode': 'Hellmodus',
    'settings.language': 'Sprache',
    'settings.selectLanguage': 'Sprache auswählen',
    'settings.accessibility': 'Barrierefreiheit',
    'settings.textAndIconSize': 'Text- und Symbolgröße',
    'settings.help': 'Hilfe',
    'settings.replayTutorial': 'Tutorial erneut abspielen',
    'settings.system': 'System',
    'settings.resetViewer': 'Viewer zurücksetzen',
    'settings.option.small': 'Klein',
    'settings.option.medium': 'Mittel',
    'settings.option.large': 'Groß',
    'step.label': 'Schritt',
    'step.of': 'von',
    'header.adminMode': 'Admin-Modus',
    'header.adminModeEnabled': 'Admin-Modus aktiviert',
    'header.administrativeView': 'Administrative Ansicht',
    'header.settings': 'Einstellungen',
    'header.close': 'Schließen',
    'panel.tools': 'Werkzeuge',
    'panel.parts': 'Teile',
    'panel.items': 'Elemente',
    'panel.searchTools': 'Werkzeuge suchen...',
    'panel.searchParts': 'Teile suchen...',
    'panel.viewDetailsFor': 'Details anzeigen für',
    'toolbar.tools': 'Werkzeuge',
    'toolbar.parts': 'Teileliste',
    'toolbar.narrationOn': 'Erzählung an',
    'toolbar.narrationOff': 'Erzählung aus',
    'toolbar.timeline': 'Zeitstrahl-Editor (Admin)',
    'toolbar.stepVisibility': 'Schrittsichtbarkeit',
    'toolbar.uploadGlb': 'Pipeline-GLB hochladen',
    'toolbar.tutorial': 'Anleitung',
    'toolbar.customerSupport': 'Kundensupport',
  },
  zh: {
    'settings.title': '设置',
    'settings.close': '关闭设置',
    'settings.appearance': '外观',
    'settings.theme': '主题',
    'settings.darkMode': '深色模式',
    'settings.lightMode': '浅色模式',
    'settings.language': '语言',
    'settings.selectLanguage': '选择语言',
    'settings.accessibility': '无障碍',
    'settings.textAndIconSize': '文本和图标大小',
    'settings.help': '帮助',
    'settings.replayTutorial': '重播教程',
    'settings.system': '系统',
    'settings.resetViewer': '重置查看器',
    'settings.option.small': '小',
    'settings.option.medium': '中',
    'settings.option.large': '大',
    'step.label': '步骤',
    'step.of': '/',
    'header.adminMode': '管理员模式',
    'header.adminModeEnabled': '管理员模式已启用',
    'header.administrativeView': '管理视图',
    'header.settings': '设置',
    'header.close': '关闭',
    'panel.tools': '工具',
    'panel.parts': '部件',
    'panel.items': '项',
    'panel.searchTools': '搜索工具...',
    'panel.searchParts': '搜索部件...',
    'panel.viewDetailsFor': '查看详情：',
    'toolbar.tools': '工具',
    'toolbar.parts': '零件列表',
    'toolbar.narrationOn': '旁白开启',
    'toolbar.narrationOff': '旁白关闭',
    'toolbar.timeline': '时间轴编辑器（管理员）',
    'toolbar.stepVisibility': '步骤可见性',
    'toolbar.uploadGlb': '上传 Pipeline GLB',
    'toolbar.tutorial': '教程',
    'toolbar.customerSupport': '客户支持',
  },
};

export function t(language: LanguageCode, key: I18nKey): string {
  return dictionary[language]?.[key] ?? dictionary.en[key] ?? key;
}
